import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { LayoutDashboard, TrendingUp, Database, Brain, BarChart3, LogOut, ChevronLeft, ChevronRight, Bell, Users, Map, DollarSign, Globe, Warehouse, BellRing, Wallet, Star, LineChart, Bot, Sun, Moon, Newspaper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/market", label: "Market Overview", icon: TrendingUp },
  { path: "/portfolio", label: "Portfolio", icon: Wallet },
  { path: "/watchlists", label: "Watchlists", icon: Star },
  { path: "/historical", label: "Historical", icon: LineChart },
  { path: "/ai-assistant", label: "AI Assistant", icon: Bot },
  { path: "/news-sentiment", label: "News Sentiment", icon: Newspaper },
  { path: "/explorer", label: "Data Explorer", icon: Database },
  { path: "/price-signals", label: "Price Signals", icon: BarChart3 },
  { path: "/price-alerts", label: "Price Alerts", icon: BellRing },
  { path: "/financing", label: "Financing", icon: DollarSign },
  { path: "/trade-flow", label: "Trade Flow", icon: Globe },
  { path: "/geospatial", label: "Geospatial", icon: Map },
  { path: "/intelligence", label: "AI Insights", icon: Brain },
  { path: "/alerts", label: "Alerts", icon: Bell },
];

export const Sidebar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/alerts?limit=1");
        setUnreadAlerts(data.unread_count || 0);
      } catch (_) {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user");
    window.location.href = "/login";
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-testid="sidebar"
        className="fixed left-0 top-0 h-screen bg-[#0a0a0c] border-r border-zinc-800 flex flex-col z-50 transition-all duration-200"
        style={{ width: collapsed ? 64 : 240 }}
      >
        {/* Logo */}
        <div className="p-4 border-b border-zinc-800 flex items-center gap-3">
          <img src="/uwatu-logo.png" alt="UWATU" className="w-10 h-10 object-contain flex-shrink-0" />
          {!collapsed && (
            <div>
              <h1 className="font-heading text-sm font-bold uppercase tracking-wider text-zinc-100">UWATU</h1>
              <p className="text-[9px] text-zinc-500 uppercase tracking-widest">Commodities Intelligence</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            const isAlerts = item.path === "/alerts";
            const btn = (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2.5 text-xs uppercase tracking-wider transition-all duration-100 relative ${
                  isActive
                    ? "bg-zinc-800/80 text-[#00ff41] border-l-2 border-[#00ff41]"
                    : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 border-l-2 border-transparent"
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {!collapsed && <span className="font-mono">{item.label}</span>}
                {isAlerts && unreadAlerts > 0 && (
                  <span data-testid="unread-badge" className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#ff3b30] text-white text-[8px] font-mono font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {unreadAlerts > 9 ? "9+" : unreadAlerts}
                  </span>
                )}
              </Link>
            );
            return collapsed ? (
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>{btn}</TooltipTrigger>
                <TooltipContent side="right" className="bg-zinc-800 text-zinc-100 border-zinc-700 text-xs">
                  {item.label} {isAlerts && unreadAlerts > 0 ? `(${unreadAlerts})` : ""}
                </TooltipContent>
              </Tooltip>
            ) : btn;
          })}
        </nav>

        {/* Bottom */}
        <div className="border-t border-zinc-800 p-3">
          {!collapsed && user.name && (
            <div className="mb-3 px-2">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Operator</p>
              <p className="text-xs text-zinc-300 font-mono truncate">{user.name}</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              data-testid="collapse-sidebar-btn"
              variant="ghost"
              size="sm"
              className="text-zinc-500 hover:text-zinc-300 p-1 h-7 w-7"
              onClick={onToggle}
            >
              {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid="theme-toggle-btn"
                  variant="ghost"
                  size="sm"
                  className="text-zinc-500 hover:text-[#00ff41] p-1 h-7 w-7"
                  onClick={toggleTheme}
                >
                  {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-zinc-800 text-zinc-100 border-zinc-700 text-xs">
                {theme === "dark" ? "Light Mode" : "Dark Mode"}
              </TooltipContent>
            </Tooltip>
            {!collapsed && (
              <Button
                data-testid="logout-btn"
                variant="ghost"
                size="sm"
                className="text-zinc-500 hover:text-red-400 p-1 h-7 flex-1 justify-start gap-2 text-xs"
                onClick={handleLogout}
              >
                <LogOut className="w-3.5 h-3.5" /> Logout
              </Button>
            )}
          </div>
        </div>
      </aside>
    </TooltipProvider>
  );
};
